<script lang="ts">

import { onMount } from "svelte";
import { link } from "svelte-routing";
import { fly } from "svelte/transition";
import { expoInOut } from "svelte/easing";

import { fetchNewsUpdate } from "../services/updates";
import type { NewsUpdate } from "../structs";

// Fetch homepage updates from WordPress
const newsUpdateFetch = new Promise(async (resolve: (data: NewsUpdate[]) => void) => {
    const data = await fetchNewsUpdate();
    resolve(data);
});


let tagLineIndex = 0;
const tagLines = [
    "Pathways for a safer world",
    "A holistic vision for sustainability",
    "A sustainable future through life-cycle thinking",
    "Making better choices for future generations"
]

// Cycles through the landing title tag lines
const tagLineCycle = () => {
    setInterval(() => {
        tagLineIndex = (tagLineIndex+1 >= tagLines.length) ? 0 : tagLineIndex+1;
    }, 5000);
}

let researchClusters;

// Smoothly scroll to the position of research clusters
export const scrollToResearchAnchor = () => {
    document.body.scroll({
        behavior: 'smooth',
        top: researchClusters.offsetTop
    });
}

// Check if URL contains ?anchor=research
const checkResearchAnchor = () => {
    if (isURLParameter("anchor", "research")) {
        scrollToResearchAnchor()
    }

    function isURLParameter(key, value): boolean {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get(key) === null) return false;
        return (urlParams.get(key) === value) ? true : false;
    }
}

onMount(async () => {
    tagLineCycle();
    setTimeout(() => checkResearchAnchor(), 500);
});

</script>

<section id="landing">
    <div class="top-background">
        <div class="details">
            <!-- Cycling heading -->
            <div class="title-wrapper">
                {#key tagLineIndex}
                    <h1 class="title" 
                        in:fly={{y: 100, duration: 1500, easing: expoInOut}} 
                        out:fly={{y: -100, duration: 1500, easing: expoInOut}}
                        on:outrostart={e => {
                            e.currentTarget.style.position = "absolute";
                            e.currentTarget.style.width = "100%";
                            e.currentTarget.style.top = "0";
                            e.currentTarget.style.left = "0";
                        }}>
                        {tagLines[tagLineIndex]}
                    </h1>
                {/key}
            </div>
            <h2 class="subtitle">Life Cycle Management Laboratory</h2>
            <div class="action-wrapper">
                <a href="/?anchor=research" on:click="{scrollToResearchAnchor}" class="action-button accent" use:link>Our Research</a>
            </div>
        </div>
        <div class="background-video">
            <video autoplay muted loop class="back-video">
                <source src="images/home-backgrounds/landing-video.mp4" type="video/mp4">
            </video>
        </div>
    </div>
</section>

<section id="updates">
    {#await newsUpdateFetch then newsUpdates}
        {#if newsUpdates.length > 0}
            <div class="dark-mid-container">
                {#each newsUpdates as update}
                    <h2 class="subtitle">{ @html update.title }</h2>
                    <div class="details">{ @html update.description } </div>
                {/each}
            </div>
        {/if}
    {/await}
</section>

<section id="research-clusters" bind:this={researchClusters}>
    <div class="content">
        <div class="top">
            <h1 class="subtitle">Research Clusters</h1>
            <h4 style="font-weight: normal;">Sustainable future for industries & communities through research and innovation.</h4>
        </div>
        <div class="cluster-cards">
            <ul>
                <!-- Urban Development -->
                <li>
                    <div class="wrapper">
                        <h2 class="subtitle">Urban Development</h2>
                        <p> 
                            LCML-energy research team lead efforts in developing energy related decision making solutions to address challenges in different sectors. We produce unique solutions to energy related decision-making problems by combining our expertise in asset management and energy performance evaluation with life cycle thinking. Current research in the lab covers a variety of problems including buildings energy efficiency improvements and retrofit planning, life cycle assessment of energy technologies and building materials, energy system feasibility assessment, and community energy planning. The reasons behind the practical utility and timelines of our research are the strong partnerships and collaborations with local municipalities and energy companies.
                        </p>
                        <a href="/projects/urban-development/completed" class="action-button accent" use:link>Explore More</a>
                    </div>
                    <img src="images/home-backgrounds/urban.jpg" alt="Background">
                </li>
                <!-- Water Systems -->
                <li>
                    <div class="wrapper right">
                        <h2 class="subtitle">Water Systems</h2>
                        <p> 
                            The world's water resources are facing pressures from unprecedented population growth, increasing urbanization, and climate change. These drastic changes have created many challenges to water services in urban areas. These much-needed water services are responsible for providing safe drinking water, ensuring adequate wastewater treatment, effectively managing stormwater, and supporting sustainable water resource management to achieve environmentally friendly development and production. Each of these services require complex infrastructure and scientific management systems. The LCML-Water Research Team is contributing to the knowledge and advancement of water systems management. More importantly, the team's research is helping shift communities toward seamless, integrated water systems with the goal of net-zero water status, in terms of water supply, pollution prevention, and sustainable urban ecology. Past and ongoing research has covered such topics as: drinking water risk assessment, water distribution network management, low-impact stormwater management, and wastewater treatment and reuse. These water system-related research projects can provide effective solutions to meet the needs of various stakeholder groups.
                        </p>
                        <a href="/projects/water-systems/completed" class="action-button accent" use:link>Explore More</a>
                    </div>
                    <img src="images/home-backgrounds/water.jpg" alt="Background">
                </li>
                <!-- Energy Systems -->
                <li>
                    <div class="wrapper">
                        <h2 class="subtitle">Energy Systems</h2>
                        <p> 
                            LCML-energy research team lead efforts in developing energy related decision making solutions to address challenges in different sectors. We produce unique solutions to energy related decision-making problems by combining our expertise in asset management and energy performance evaluation with life cycle thinking. Current research in the lab covers a variety of problems including buildings energy efficiency improvements and retrofit planning, life cycle assessment of energy technologies and building materials, energy system feasibility assessment, and community energy planning. The reasons behind the practical utility and timelines of our research are the strong partnerships and collaborations with local municipalities and energy companies.
                        </p>
                        <a href="/projects/energy-systems/completed" class="action-button accent" use:link>Explore More</a>
                    </div>
                    <img src="images/home-backgrounds/energy.jpg" alt="Background">
                </li>
                <!-- Construction & Asset Management -->
                <li>
                    <div class="wrapper right">
                        <h2 class="subtitle">Construction & Asset Management</h2>
                        <p> 
                            Canadian infrastructure stock is aging and have deteriorated considerably. A systematic and unique asset management approach has gained an attention from decision makers due to funding limitations and resource scarcity for new infrastructure. Life cycle asset management (LCAM) is a novelty concept to maintain a desired service level of an asset at the lowest life cycle cost while complying with legal obligations and standards. LCML- Construction and asset management research team lead efforts in developing life cycle thinking based construction and asset management solutions to ensure optimized utilization of asset in its entire life cycle. We produce exclusive solutions for transport, highway, building and water related infrastructure asset management practices by combining our expertise on construction management and decision-making techniques. Current research in the lab covers a verity of problems including asset management framework development and proposing best construction management practices for urban water supply systems, public buildings, oil and gas pipelines, and transport infrastructure development projects.
                        </p>
                        <a href="/projects/construction-and-asset-management/completed" class="action-button accent" use:link>Explore More</a>
                    </div>
                    <img src="images/home-backgrounds/construction.jpg" alt="Background">
                </li>
                <!-- Industrial Products & Processes -->
                <li>
                    <div class="wrapper">
                        <h2 class="subtitle">Industrial Products & Processes</h2>
                        <p>
                            Industrial activities are a highly-skilled and highly-technical sector representing a major part of Canada’s economy. Although industries are fast growing and innovative players, the sector still faces many issues related to quality control, process and scheduling optimization, supply chain and risk management, productivity and sustainability improvement. The Life Cycle Management Laboratory research team conducts research and industry projects to develop sustainable solutions to process optimization, risk assessment and quality control management, employing state-of-the-art software, research techniques and life-cycle thinking. Currently, the lab is working on manufacturing process optimization, risk assessment of fracking fluids, and integrity management of oil and gas infrastructure.
                        </p>
                        <a href="/projects/industrial-products-and-processes/completed" class="action-button accent" use:link>Explore More</a>
                    </div>
                    <img src="images/home-backgrounds/industrial.jpg" alt="Background">
                </li>
            </ul>
        </div>
    </div>
</section>




<style lang="sass">

@import "../consts.sass"
@include page-header()
@include action-button()

.top-background
    position: relative
    height: auto
    padding-bottom: 4em
    height: 65vh
    display: flex
    flex-direction: column
    justify-content: flex-end
    background: linear-gradient(transparent 70%, #00162e 100%)
    
    @media only screen and (max-width: 979px) 
        margin-left: -20px
        margin-right: -20px


    .background-video
        position: absolute
        top: 50%
        transform: translate(0, -50%)
        left: 0
        width: 100%
        height: 100%
        z-index: -1

        video
            width: 100%
            height: 100%
            object-fit: cover


    .details
        width: 100%
        position: relative
        color: white !important

        .title-wrapper
            width: 100%
            position: relative
            overflow: hidden
            text-shadow: 0px 0px 20px rgba(0,0,0,8)

        .title
            font-size: 5.6em
            color: white

        .subtitle
            font-size: 2em
            color: white
            font-weight: bold
            margin-top: 10px
            text-shadow: 0px 0px 20px rgba(0,0,0,6)

        .action-wrapper
            display: flex
            flex-direction: row
            margin-top: 4em
            color: white

.title
    font-size: 5.4em
    line-height: 100%
    font-weight: lighter

.subtitle
    font-size: 3em
    font-weight: lighter
    letter-spacing: 0.1em
    text-transform: uppercase


.dark-mid-container
    background-color: #00162e
    padding-top: 5em !important
    padding-bottom: 5em !important
    display: flex
    flex-direction: column
    justify-content: center
    align-items: flex-start
    color: white

    *
        color: white

    @media only screen and (max-width: 979px) 
        margin-left: -20px
        margin-right: -20px

    h2,.details
        color: white

    h2.subtitle
        margin-bottom: 1em

    .details
        font-size: 1.2em
        line-height: 150%
        margin-bottom: 1em

        :global(a)
            @include button-accent()
            margin-top: 3em


#research-clusters .cluster-cards
    margin-top: 5em

    ul
        list-style-type: none
        margin: 0

        li
            position: relative
            margin-bottom: 3em
            border-radius: 0.2em
            overflow: hidden
            z-index: 1

            .wrapper
                background-color: rgba(0,0,0,0.8)
                padding: 5em
                box-sizing: border-box
                width: 50%

                &.right
                    float: right

                @media only screen and (max-width: 950px)
                    width: 100% 
                    background-color: rgba(0,0,0,0.6)

            h2,p
                color: white
                margin-bottom: 2rem
                text-shadow: 0px 0px 10px rgba(0,0,0,1)
                
            img
                position: absolute
                top: 0
                left: 0
                width: 100%
                height: 100%
                object-fit: cover
                z-index: -1


.content, .dark-mid-container
    padding: 6em 2em
    box-sizing: border-box

    @media only screen and (min-width: 1200px) 
        padding-right: 10vw
        padding-left: 10vw

        
        
</style>